// Per-project member management dialog — see migration 0011.
import { useMemo, useState } from "react";
import { Check, Loader2, UserPlus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useMembers } from "@/hooks/use-members";
import {
  useInviteProjectMember,
  useProjectMembers,
  useRemoveProjectMember,
} from "@/hooks/use-project-members";
import { canCreateProject, roleDisplayName } from "@/lib/api-types";
import { authErrorMessage } from "@/lib/auth-errors";
import { cn } from "@/lib/utils";

type Props = {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Per-project member management — locked 2026-07-14, migration 0011.
 *
 * Backed by three real endpoints:
 *   GET    /api/editor/projects/:id/members       → list
 *   POST   /api/editor/projects/:id/members       → add existing workspace member
 *   DELETE /api/editor/projects/:id/members/:uid  → remove
 *
 * All three are ADMIN + FULL_ACCESS only server-side. Non-admin callers won't
 * open this dialog today (the button is gated in the top bar), but we still
 * render a defensive "only admins can manage members" state.
 *
 * "Invite" here is misleading — an entry must ALREADY be a workspace member.
 * Adding a brand-new email to the workspace goes through the Manage Members
 * dialog on the projects page. This is intentional (locked with user): keeps
 * per-project ACL narrow, prevents accidentally granting workspace-wide access.
 */
export function InviteToProjectDialog({ projectId, projectName, open, onOpenChange }: Props) {
  const { user, role } = useAuth();
  const canManage = canCreateProject(role);

  // Query the workspace member list (feeds the picker) + the current project
  // member list (feeds the "already invited" section). Both gated on `open` so
  // closing the dialog doesn't keep the queries alive.
  const membersQuery = useMembers(open && canManage);
  const projectMembersQuery = useProjectMembers(projectId, open && canManage);

  const invite = useInviteProjectMember(projectId);
  const remove = useRemoveProjectMember(projectId);

  const [query, setQuery] = useState("");

  const invitedIds = useMemo(
    () => new Set((projectMembersQuery.data ?? []).map((m) => m.userId)),
    [projectMembersQuery.data],
  );

  // Filter: workspace members not already on the project, not self, not ADMIN
  // (ADMIN can access everything anyway — no point inviting them).
  const suggestions = useMemo(() => {
    const rows = membersQuery.data ?? [];
    const trimmed = query.trim().toLowerCase();
    return rows.filter((m) => {
      if (m.id === user?.id) return false;
      if (invitedIds.has(m.id)) return false;
      if (m.role === "ADMIN") return false;
      if (!trimmed) return true;
      return (
        m.email.toLowerCase().includes(trimmed) ||
        (m.name || "").toLowerCase().includes(trimmed)
      );
    });
  }, [membersQuery.data, query, invitedIds, user?.id]);

  const inviteError = invite.isError ? authErrorMessage(invite.error) : null;
  const removeError = remove.isError ? authErrorMessage(remove.error) : null;

  function handleAdd(userId: string) {
    invite.mutate(userId, { onSuccess: () => setQuery("") });
  }

  function handleClose(next: boolean) {
    if (!next) setQuery("");
    onOpenChange(next);
  }

  // Non-admin defensive state — the button that opens this shouldn't be visible
  // for EDIT / EDIT_SHARE anyway.
  if (!canManage) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite to "{projectName}"</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Only Workspace Admin and Full Access users can manage project
            members. Ask one of them to add you.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite to "{projectName}"</DialogTitle>
          <DialogDescription>
            Add existing workspace members. To invite a brand-new person, use
            Manage Members on the projects page first.
          </DialogDescription>
        </DialogHeader>

        {/* Picker */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-project-search">Search workspace members</Label>
          <Input
            id="invite-project-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name or email"
            autoComplete="off"
          />
        </div>

        <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
          {membersQuery.isLoading ? (
            <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading members…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              {query
                ? "No matching workspace members."
                : "Everyone in the workspace is already on this project."}
            </div>
          ) : (
            suggestions.map((m) => {
              const isAdding = invite.isPending && invite.variables === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleAdd(m.id)}
                  disabled={invite.isPending}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left last:border-b-0",
                    "hover:bg-secondary/60 disabled:opacity-60",
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {m.name || m.email}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {m.email} · {roleDisplayName(m.role)}
                    </div>
                  </div>
                  {isAdding ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}

        {/* Current project members */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">On this project</p>
            {projectMembersQuery.isFetching && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="rounded-lg border border-border">
            {(projectMembersQuery.data ?? []).length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">
                No members invited yet. Workspace Admin and Full Access users
                always have access.
              </div>
            ) : (
              (projectMembersQuery.data ?? []).map((m) => {
                const isRemoving = remove.isPending && remove.variables === m.userId;
                return (
                  <div
                    key={m.userId}
                    className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {m.name || m.email}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {m.email} · {roleDisplayName(m.role)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove.mutate(m.userId)}
                      disabled={remove.isPending}
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-destructive disabled:opacity-60"
                      title="Remove from project"
                      aria-label={`Remove ${m.email} from project`}
                    >
                      {isRemoving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <X className="h-3.5 w-3.5" strokeWidth={2} />
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          {removeError && <p className="text-sm text-destructive">{removeError}</p>}
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={2.25} />
          Only affects restricted projects. Public projects are already visible
          to every workspace member.
        </div>
      </DialogContent>
    </Dialog>
  );
}
